import React, { useState, useEffect } from 'react';
import { Play, Download, Copy, Check, AlertCircle, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchOddsWithLinks, fetchAvailableSports, fetchPlayerPropsForEvents, BOOKMAKERS } from '../../services/oddsApiClient';
import {
  logStructured,
  formatAPIResponse,
  exportToJSON,
  copyToClipboard,
  countLinks,
  validateLink
} from './apiTestHelpers';

/**
 * OddsAPIExplorer - Interactive test dashboard for exploring Odds API
 */
const OddsAPIExplorer = ({ onDataFetched }) => {
  // State management
  const [availableSports, setAvailableSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState('americanfootball_nfl');
  const [selectedMarkets, setSelectedMarkets] = useState(['h2h', 'spreads', 'totals']);
  const [selectedBookmakers, setSelectedBookmakers] = useState(Object.keys(BOOKMAKERS).slice(0, 3));
  const [includeLinks, setIncludeLinks] = useState(true);
  const [includeSids, setIncludeSids] = useState(true);
  const [includeBetLimits, setIncludeBetLimits] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkStats, setLinkStats] = useState(null);

  // Expanded state for game cards
  const [expandedGames, setExpandedGames] = useState(new Set());

  // Available markets
  const availableMarkets = [
    { key: 'h2h', label: 'Moneyline (H2H)' },
    { key: 'spreads', label: 'Spreads' },
    { key: 'totals', label: 'Totals (Over/Under)' },
    { key: 'player_points', label: 'Player Points' },
    { key: 'player_rebounds', label: 'Player Rebounds' },
    { key: 'player_assists', label: 'Player Assists' }
  ];

  // Fetch available sports on mount
  useEffect(() => {
    const loadSports = async () => {
      try {
        logStructured('API_EXPLORER', 'Fetching available sports...');
        const sports = await fetchAvailableSports();
        setAvailableSports(sports);
        logStructured('API_EXPLORER', `Loaded ${sports.length} sports`, sports);
      } catch (error) {
        logStructured('API_EXPLORER', 'Error fetching sports', error);
        // Use fallback sports list
        setAvailableSports([
          { key: 'americanfootball_nfl', title: 'NFL', group: 'American Football' },
          { key: 'basketball_nba', title: 'NBA', group: 'Basketball' },
          { key: 'baseball_mlb', title: 'MLB', group: 'Baseball' },
          { key: 'icehockey_nhl', title: 'NHL', group: 'Ice Hockey' },
          { key: 'soccer_epl', title: 'EPL', group: 'Soccer' }
        ]);
      }
    };

    loadSports();
  }, []);

  // Handle market selection
  const toggleMarket = (marketKey) => {
    setSelectedMarkets(prev =>
      prev.includes(marketKey)
        ? prev.filter(m => m !== marketKey)
        : [...prev, marketKey]
    );
  };

  // Handle bookmaker selection
  const toggleBookmaker = (bookmakerKey) => {
    setSelectedBookmakers(prev =>
      prev.includes(bookmakerKey)
        ? prev.filter(b => b !== bookmakerKey)
        : [...prev, bookmakerKey]
    );
  };

  // Fetch odds data
  const handleFetchOdds = async () => {
    setIsLoading(true);
    setError(null);
    setApiResponse(null);
    setLinkStats(null);

    logStructured('API_EXPLORER', '=== FETCHING ODDS ===');
    logStructured('API_EXPLORER', 'Sport', selectedSport);
    logStructured('API_EXPLORER', 'Markets', selectedMarkets);
    logStructured('API_EXPLORER', 'Bookmakers', selectedBookmakers);
    logStructured('API_EXPLORER', 'Options', {
      includeLinks,
      includeSids,
      includeBetLimits
    });

    try {
      const options = {
        bookmakers: selectedBookmakers,
        markets: selectedMarkets.join(','),
        oddsFormat: 'american',
        regions: 'us'
      };

      // Fetch game-level odds first
      let response = await fetchOddsWithLinks(selectedSport, options);
      logStructured('API_EXPLORER', `✅ Received ${response.length} games`);

      // Check if player props were requested
      const playerPropsMarkets = selectedMarkets.filter(m =>
        m.includes('player_')
      );

      if (playerPropsMarkets.length > 0 && response.length > 0) {
        logStructured('API_EXPLORER', `📊 Fetching player props for ${playerPropsMarkets.length} markets...`);

        const playerPropsOptions = {
          bookmakers: selectedBookmakers,
          markets: playerPropsMarkets.join(','),
          oddsFormat: 'american',
          regions: 'us'
        };

        response = await fetchPlayerPropsForEvents(response, selectedSport, playerPropsOptions);
        logStructured('API_EXPLORER', `✅ Added player props to ${response.length} games`);
      }

      setApiResponse(response);

      // Share data with parent component
      if (onDataFetched) {
        onDataFetched(response);
      }

      // Calculate link statistics
      const stats = countLinks(response);
      setLinkStats(stats);
      logStructured('API_EXPLORER', 'Link Statistics', stats);

    } catch (err) {
      logStructured('API_EXPLORER', '❌ Error fetching odds', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (apiResponse) {
      const success = await copyToClipboard(formatAPIResponse(apiResponse));
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  // Handle export
  const handleExport = () => {
    if (apiResponse) {
      const filename = `odds_${selectedSport}_${Date.now()}`;
      exportToJSON(apiResponse, filename);
    }
  };

  // Toggle game expansion
  const toggleGameExpansion = (gameId) => {
    setExpandedGames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gameId)) {
        newSet.delete(gameId);
      } else {
        newSet.add(gameId);
      }
      return newSet;
    });
  };

  // Test a deep link
  const testLink = (url) => {
    logStructured('API_EXPLORER', 'Testing link', url);
    const validation = validateLink(url);
    logStructured('API_EXPLORER', 'Link validation', validation);

    if (validation.valid) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      alert(`Invalid link: ${validation.reason}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            Odds API Explorer
          </h1>
          <p className="text-slate-400">
            Interactive testing dashboard for exploring The Odds API deep links
          </p>
        </div>

        {/* Configuration Panel */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Configuration</h2>

          {/* Sport Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select Sport
            </label>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {availableSports.map((sport) => (
                <option key={sport.key} value={sport.key}>
                  {sport.title} ({sport.group})
                </option>
              ))}
            </select>
          </div>

          {/* Market Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select Markets
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availableMarkets.map((market) => (
                <label
                  key={market.key}
                  className="flex items-center gap-2 bg-slate-700 border border-slate-600 rounded px-3 py-2 cursor-pointer hover:bg-slate-600 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedMarkets.includes(market.key)}
                    onChange={() => toggleMarket(market.key)}
                    className="rounded text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-white">{market.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Bookmaker Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select Bookmakers
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(BOOKMAKERS).map(([key, bookmaker]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 bg-slate-700 border border-slate-600 rounded px-3 py-2 cursor-pointer hover:bg-slate-600 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedBookmakers.includes(key)}
                    onChange={() => toggleBookmaker(key)}
                    className="rounded text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-white">{bookmaker.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              API Options
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeLinks}
                  onChange={(e) => setIncludeLinks(e.target.checked)}
                  className="rounded text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-sm text-white">Include Links</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSids}
                  onChange={(e) => setIncludeSids(e.target.checked)}
                  className="rounded text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-sm text-white">Include SIDs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeBetLimits}
                  onChange={(e) => setIncludeBetLimits(e.target.checked)}
                  className="rounded text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-sm text-white">Include Bet Limits</span>
              </label>
            </div>
          </div>

          {/* Fetch Button */}
          <button
            onClick={handleFetchOdds}
            disabled={isLoading || selectedMarkets.length === 0 || selectedBookmakers.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 disabled:from-slate-600 disabled:to-slate-600 text-white font-medium py-3 px-6 rounded-lg transition-all disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Fetching Odds...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Fetch Odds</span>
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-400 font-medium mb-1">Error</h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Link Statistics */}
        {linkStats && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Link Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-emerald-400">{linkStats.totalGames}</div>
                <div className="text-sm text-slate-400">Games</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-cyan-400">{linkStats.linksCount.event}</div>
                <div className="text-sm text-slate-400">Event Links</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-400">{linkStats.linksCount.market}</div>
                <div className="text-sm text-slate-400">Market Links</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-400">{linkStats.linksCount.outcome}</div>
                <div className="text-sm text-slate-400">Outcome Links</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Panel */}
        {apiResponse && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Results ({apiResponse.length} games)
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy JSON'}</span>
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm">Export</span>
                </button>
              </div>
            </div>

            {/* Game Cards */}
            <div className="space-y-4">
              {apiResponse.map((game) => (
                <div key={game.id} className="bg-slate-700/50 border border-slate-600 rounded-lg overflow-hidden">
                  {/* Game Header */}
                  <button
                    onClick={() => toggleGameExpansion(game.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-left">
                        <div className="text-white font-medium">
                          {game.away_team} @ {game.home_team}
                        </div>
                        <div className="text-sm text-slate-400">
                          {new Date(game.commence_time).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {expandedGames.has(game.id) ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {expandedGames.has(game.id) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-slate-600"
                      >
                        <div className="p-4">
                          {/* Game Odds */}
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-white mb-3 border-b border-slate-600 pb-2">
                              Game Odds
                            </h3>
                            {Object.entries(game.bookmakers).map(([bookmakerKey, bookmakerData]) => (
                              <div key={bookmakerKey} className="mb-4 last:mb-0">
                                <h4 className="text-sm font-medium text-emerald-400 mb-2">
                                  {BOOKMAKERS[bookmakerKey]?.name || bookmakerKey}
                                </h4>

                                {/* Markets */}
                                {Object.entries(bookmakerData.markets).map(([marketKey, marketData]) => (
                                  <div key={marketKey} className="mb-3 last:mb-0 bg-slate-800/50 rounded p-3">
                                    <div className="text-xs font-medium text-cyan-400 mb-2 uppercase">
                                      {marketKey}
                                    </div>

                                    {/* Outcomes */}
                                    <div className="space-y-2">
                                      {Object.entries(marketData.outcomes).map(([outcomeName, outcomeData]) => (
                                        <div key={outcomeName} className="flex items-center justify-between text-sm">
                                          <span className="text-white">
                                            {outcomeName}
                                            {outcomeData.point && (
                                              <span className="ml-2 text-slate-400">
                                                ({outcomeData.point > 0 ? '+' : ''}{outcomeData.point})
                                              </span>
                                            )}
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <span className="text-emerald-400 font-medium">
                                              {outcomeData.price > 0 ? '+' : ''}{outcomeData.price}
                                            </span>
                                            {outcomeData.link && (
                                              <button
                                                onClick={() => testLink(outcomeData.link)}
                                                className="p-1 hover:bg-slate-700 rounded transition-colors"
                                                title="Test deep link"
                                              >
                                                <ExternalLink className="w-4 h-4 text-cyan-400" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>

                          {/* Player Props */}
                          {game.playerProps && Object.keys(game.playerProps.bookmakers || {}).length > 0 && (
                            <div className="mb-4">
                              <h3 className="text-lg font-semibold text-purple-400 mb-3 border-b border-slate-600 pb-2">
                                🏀 Player Props
                              </h3>
                              {Object.entries(game.playerProps.bookmakers).map(([bookmakerKey, bookmakerData]) => (
                                <div key={bookmakerKey} className="mb-4 last:mb-0">
                                  <h4 className="text-sm font-medium text-purple-300 mb-2">
                                    {BOOKMAKERS[bookmakerKey]?.name || bookmakerKey}
                                  </h4>

                                  {/* Player Props Markets */}
                                  {Object.entries(bookmakerData.markets).map(([marketKey, marketData]) => (
                                    <div key={marketKey} className="mb-3 last:mb-0 bg-purple-900/20 rounded p-3 border border-purple-800/30">
                                      <div className="text-xs font-medium text-purple-300 mb-3 uppercase">
                                        {marketKey.replace('player_', '')} Props
                                      </div>

                                      {/* Group by player */}
                                      {(() => {
                                        const playerGroups = {};
                                        Object.entries(marketData.outcomes).forEach(([key, outcome]) => {
                                          const player = outcome.description || 'Unknown';
                                          if (!playerGroups[player]) {
                                            playerGroups[player] = [];
                                          }
                                          playerGroups[player].push({ key, ...outcome });
                                        });

                                        return Object.entries(playerGroups).map(([player, outcomes]) => (
                                          <div key={player} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b border-slate-700 last:border-0">
                                            <div className="text-sm font-medium text-white mb-2">{player}</div>
                                            <div className="grid grid-cols-2 gap-2">
                                              {outcomes.map((outcome) => (
                                                <div key={outcome.key} className="flex items-center justify-between bg-slate-800/70 rounded p-2">
                                                  <div className="flex flex-col">
                                                    <span className="text-xs text-slate-400">{outcome.name}</span>
                                                    <span className="text-sm text-white font-medium">
                                                      {outcome.point}
                                                    </span>
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-medium ${outcome.price > 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                                      {outcome.price > 0 ? '+' : ''}{outcome.price}
                                                    </span>
                                                    {outcome.link && (
                                                      <button
                                                        onClick={() => testLink(outcome.link)}
                                                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                                                        title="Test player prop link"
                                                      >
                                                        <ExternalLink className="w-3 h-3 text-purple-400" />
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Raw JSON (collapsed) */}
                          <details className="mt-4">
                            <summary className="cursor-pointer text-sm text-slate-400 hover:text-slate-300">
                              View Raw JSON
                            </summary>
                            <pre className="mt-2 p-3 bg-slate-900 rounded text-xs text-slate-300 overflow-x-auto">
                              {formatAPIResponse(game)}
                            </pre>
                          </details>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OddsAPIExplorer;
